console.log("SLIDER");
import { gsap } from "gsap";

const colors = [
  "#24478f",
  "#cc0000",
  "#663300",
  "#006600",
  "#cc5200",
  "#6b00b3",
];
const sliders = gsap.utils.toArray(".board-carousel-items");
const slidesArray = sliders.map((slider) =>
  gsap.utils.toArray(".board-carousel-item", slider)
);

// Get logos buttons
const buttonsArray = sliders.map((slider) => {
  let buttons = slider.parentElement.querySelectorAll("nav a");
  return gsap.utils.toArray(buttons);
});

// Get carousel sections and nav elements
const carouselSections = sliders.map((slider) => slider.closest("section"));
const navElements = sliders.map((slider) => slider.parentElement.querySelector("nav"));

buttonsArray.forEach((buttons) => {
  buttons.forEach((button, i) => {
    button.onclick = (e) => {
      e.preventDefault();
      gotoSlide(i);
    };
  });
});

const autoscrollElement = sliders[0]?.parentElement?.parentElement?.parentElement;
const autoscrollValue = autoscrollElement?.dataset.autoscroll;
const hasAutoscrollAttribute = autoscrollElement?.hasAttribute('data-autoscroll');
const shouldAutoscroll = hasAutoscrollAttribute && (autoscrollValue === "1" || autoscrollValue === "true" || autoscrollValue === true);

const keyboardInteractionValue = autoscrollElement?.dataset.keyboardInteraction;
const hasKeyboardInteractionAttribute = autoscrollElement?.hasAttribute('data-keyboard-interaction');
const shouldEnableKeyboardInteraction = hasKeyboardInteractionAttribute && (keyboardInteractionValue === "1" || keyboardInteractionValue === "true" || keyboardInteractionValue === true);

const timeout = 5000;

let goToSlideTimeout;
function board_carousel_startAutomaticSlideScroll() {
  const button = buttonsArray[0][currentIndex];

  gsap.fromTo(
    button,
    {
      "--percentage": 0,
    },
    {
      duration: timeout / 1000,
      ease: "none",
      "--percentage": 100,
    }
  );

  goToSlideTimeout = setTimeout(() => {
    let index = currentIndex + 1;
    if (index >= buttonsArray[0].length) {
      index = 0;
    }
    gotoSlide(index);
  }, timeout);
}

let currentIndex = 0;
let isTweening = false;
let scrollFocusActive = false;
let lastScrollDirection = null;
let refocusTimeout = null;

if (shouldAutoscroll) board_carousel_startAutomaticSlideScroll();

const gotoSlide = (value) => {
  clearInterval(goToSlideTimeout);

  // Update previous slide
  if (slidesArray[0][currentIndex]) {
    slidesArray[0][currentIndex].classList.remove("active");
    slidesArray[0][currentIndex].setAttribute("aria-hidden", "true");
  }
  if (buttonsArray[0][currentIndex]) {
    buttonsArray[0][currentIndex].classList.remove("active");
    buttonsArray[0][currentIndex].setAttribute("aria-selected", "false");
    buttonsArray[0][currentIndex].setAttribute("tabindex", "-1");
    // Remove blur handler from previous button if scroll focus is active
    if (scrollFocusActive) {
      buttonsArray[0][currentIndex].removeEventListener("blur", handleBlur);
    }
  }

  // Update new slide
  if (slidesArray[0][value]) {
    slidesArray[0][value].classList.add("active");
    slidesArray[0][value].setAttribute("aria-hidden", "false");
  }
  if (buttonsArray[0][value]) {
    buttonsArray[0][value].classList.add("active");
    buttonsArray[0][value].setAttribute("aria-selected", "true");
    buttonsArray[0][value].setAttribute("tabindex", "0");
    // Update refocus handler for new active button if scroll focus is active
    if (scrollFocusActive) {
      buttonsArray[0][value].focus({ preventScroll: true });
      setupRefocusHandler(buttonsArray[0][value]);
    }
  }

  currentIndex = value;

  if (shouldAutoscroll) board_carousel_startAutomaticSlideScroll();
};

// Tablist keyboard support (WAI-ARIA tabs pattern): arrow keys, Home and End
// move between the logo tabs whatever the viewport or the block options.
const tabLists = navElements.map((nav) => nav?.querySelector('[role="tablist"]'));
tabLists.forEach((tabList, sliderIndex) => {
  if (!tabList) return;
  const tabs = buttonsArray[sliderIndex];

  tabList.addEventListener("keydown", (e) => {
    const focusedIndex = tabs.indexOf(document.activeElement);
    if (focusedIndex === -1) return;

    let nextIndex = null;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = focusedIndex > 0 ? focusedIndex - 1 : tabs.length - 1;
        break;
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = focusedIndex < tabs.length - 1 ? focusedIndex + 1 : 0;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    e.preventDefault();
    e.stopPropagation();
    if (sliderIndex === 0) gotoSlide(nextIndex);
    tabs[nextIndex].focus();
  });
});

// Keyboard navigation
function handleKeyboardNavigation(e) {
  // Only work if keyboard interaction is enabled
  if (!shouldEnableKeyboardInteraction) return;
  
  // Only work on screens > 1024px
  if (window.innerWidth <= 1024) return;

  const nav = navElements[0];
  if (!nav) return;

  // Check if focus is within the carousel navigation
  const isNavFocused = nav.contains(document.activeElement);
  if (!isNavFocused) return;

  const focusedButton = document.activeElement;
  const focusedIndex = buttonsArray[0].indexOf(focusedButton);
  if (focusedIndex === -1) return;

  let handled = false;

  switch (e.key) {
    case "Enter":
    case " ":
      e.preventDefault();
      focusedButton.click();
      handled = true;
      break;
  }

  return handled;
}

// Get common focus calculation variables
function getFocusVariables() {
  const section = carouselSections[0];
  const nav = navElements[0];
  if (!section || !nav) return null;

  const header = document.querySelector(".header");
  const headerHeight = header ? header.offsetHeight : 0;
  const headerTop = header ? header.getBoundingClientRect().top : 0;
  const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
  const headerBottomWithBuffer = headerBottom + 20;

  const sectionRect = section.getBoundingClientRect();
  const navRect = nav.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const viewport50Percent = viewportHeight * 0.5;
  const viewport80Percent = viewportHeight * 0.8;

  const navTop = navRect.top;
  const navBottom = navRect.bottom;
  const sectionTop = sectionRect.top;
  const sectionBottom = sectionRect.bottom;

  return {
    section,
    nav,
    header,
    headerHeight,
    headerTop,
    headerBottom,
    headerBottomWithBuffer,
    sectionRect,
    navRect,
    viewportHeight,
    viewport50Percent,
    viewport80Percent,
    navTop,
    navBottom,
    sectionTop,
    sectionBottom
  };
}

// Scroll-based focus management
function handleScrollFocus() {
  // Only work if keyboard interaction is enabled
  if (!shouldEnableKeyboardInteraction) {
    if (scrollFocusActive) {
      removeScrollFocus();
    }
    return;
  }
  
  // Only work on screens > 1024px
  if (window.innerWidth <= 1024) {
    if (scrollFocusActive) {
      removeScrollFocus();
    }
    return;
  }

  const vars = getFocusVariables();
  if (!vars) return;

  // Determine scroll direction
  const currentScrollY = window.scrollY || window.pageYOffset;
  const scrollDirection = currentScrollY > (window.lastScrollY || 0) ? "down" : 
                         currentScrollY < (window.lastScrollY || 0) ? "up" : lastScrollDirection;
  
  if (scrollDirection) {
    lastScrollDirection = scrollDirection;
  }
  window.lastScrollY = currentScrollY;

  // Scrolling down: Remove focus instantly when tab list goes out of viewport (below viewport)
  if (scrollDirection === "down" && vars.navTop > vars.viewportHeight && scrollFocusActive) {
    removeScrollFocus();
    return; // Exit early since nav is out of viewport
  }

  // Scrolling down: Remove focus when section bottom < 50% viewport (check this first)
  if (scrollDirection === "down" && vars.sectionBottom < vars.viewport50Percent && scrollFocusActive) {
    removeScrollFocus();
    return; // Exit early to prevent re-focusing
  }

  // Scrolling down: Remove focus when nav goes above header
  if (scrollDirection === "down" && vars.navTop < vars.headerBottomWithBuffer && scrollFocusActive) {
    console.log("removing scroll focus");
    removeScrollFocus();
    return; // Exit early to prevent re-focusing
  }

  // Scrolling down: Focus when tab list reaches 80% of viewport AND section is still above 50% viewport AND nav is below header
  if (scrollDirection === "down" && vars.navTop <= vars.viewport80Percent && vars.navTop >= vars.headerBottomWithBuffer && vars.sectionBottom >= vars.viewport50Percent && !scrollFocusActive) {
    console.log("focusing active tab");
    focusActiveTab();
  }

  // Scrolling up: Remove focus when tab list top > 80% viewport (check this first)
  if (scrollDirection === "up" && vars.navTop > vars.viewport80Percent && scrollFocusActive) {
    removeScrollFocus();
    return; // Exit early to prevent re-focusing
  }

  // Scrolling up: Focus when top of tab list reaches bottom of header + buffer
  // Only focus if nav is visible and within a reasonable range
  if (scrollDirection === "up" && vars.navTop >= vars.headerBottomWithBuffer && vars.navTop <= vars.viewport80Percent && !scrollFocusActive) {
    focusActiveTab();
  }
}

function focusActiveTab() {
  const nav = navElements[0];
  if (!nav) return;
  nav.classList.add("focused-tab-list");
  const activeButton = nav.querySelector('a[aria-selected="true"]');
  if (activeButton) {
    activeButton.focus({ preventScroll: true });
    scrollFocusActive = true;
    // Set up blur handler to refocus after 1 second
    setupRefocusHandler(activeButton);
  }
}

function setupRefocusHandler(button) {
  // Remove existing handler if any
  button.removeEventListener("blur", handleBlur);
  // Add blur handler
  button.addEventListener("blur", handleBlur);
}

function handleBlur() {
  if (!scrollFocusActive) return;

  // Clear any existing timeout
  if (refocusTimeout) {
    clearTimeout(refocusTimeout);
  }

  // Refocus after 1 second
  refocusTimeout = setTimeout(() => {
    if (scrollFocusActive) {
      const nav = navElements[0];
      if (!nav) return;
      const activeButton = nav.querySelector('a[aria-selected="true"]');
      if (activeButton) {
        activeButton.focus({ preventScroll: true });
        // Re-setup handler for the newly focused button
        setupRefocusHandler(activeButton);
      }
    }
    refocusTimeout = null;
  }, 1000);
}

function removeScrollFocus() {
  const nav = navElements[0];
  if (!nav) return;
  nav.classList.remove("focused-tab-list");
  
  // Remove blur handler from active button
  const activeButton = nav.querySelector('a[aria-selected="true"]');
  if (activeButton) {
    activeButton.removeEventListener("blur", handleBlur);
  }
  
  if (document.activeElement && nav?.contains(document.activeElement)) {
    document.activeElement.blur();
  }
  scrollFocusActive = false;
  
  // Clear refocus timeout
  if (refocusTimeout) {
    clearTimeout(refocusTimeout);
    refocusTimeout = null;
  }
}

// Throttle scroll handler
let scrollTimeout;
function throttledScrollHandler() {
  if (scrollTimeout) return;
  scrollTimeout = requestAnimationFrame(() => {
    handleScrollFocus();
    scrollTimeout = null;
  });
}

// Event listeners - only add if keyboard interaction is enabled
if (shouldEnableKeyboardInteraction) {
  document.addEventListener("keydown", handleKeyboardNavigation);
  window.addEventListener("scroll", throttledScrollHandler, { passive: true });
  window.addEventListener("resize", () => {
    checkInitialFocus();
    handleScrollFocus();
    // Reset scroll tracking on resize
    // window.lastScrollY = window.scrollY || window.pageYOffset;
  }, { passive: true });
}

// Check initial focus condition on load
function checkInitialFocus() {
  // Only work if keyboard interaction is enabled
  if (!shouldEnableKeyboardInteraction) {
    return;
  }
  
  // Only work on screens > 1024px
  if (window.innerWidth <= 1024) {
    return;
  }

  const vars = getFocusVariables();
  if (!vars || !vars.header) return;

  // Check if nav is in the focus zone: between headerBottomWithBuffer and viewport80Percent
  if (vars.navTop >= vars.headerBottomWithBuffer && vars.navTop <= vars.viewport80Percent && !scrollFocusActive) {
    console.log("initial focus check: focusing active tab");
    focusActiveTab();
  }
}

// Initialize on load - only if keyboard interaction is enabled
if (shouldEnableKeyboardInteraction) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      // window.lastScrollY = window.scrollY || window.pageYOffset;
      checkInitialFocus();
      handleScrollFocus();
    });
  } else {
    // window.lastScrollY = window.scrollY || window.pageYOffset;
    checkInitialFocus();
    handleScrollFocus();
  }
}
